import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CheckCircle2, Activity, LineChart, Cloud,
  Bell, SlidersHorizontal, Wifi, Zap, ArrowRight,
  Wind, Thermometer, Monitor, Lightbulb,
} from "lucide-react";
import { PublicLayout } from "../components/PublicLayout";
import { useLightbox } from "../components/Lightbox";

const fade = { hidden: { opacity: 0, y: 20 }, show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.06, duration: 0.45 } }) };

const FEATURE_GROUPS = [
  {
    icon: Activity,
    title: "3-Phase Energy Metering",
    color: "#00FF66",
    items: [
      "PZEM-004T × 3: V, A, W, kWh, Hz, PF per phase",
      "Modbus-RTU on Serial2: GPIO 26 (RX) / GPIO 27 (TX)",
      "Addresses pre-set: L1 → 0x01, L2 → 0x02, L3 → 0x03",
      "Readings published every 15 s to campus/energy/pzem",
      "Retained JSON; dashboard gets last value on reconnect",
      "Total 3-phase power used for fan auto-thresholds",
    ],
  },
  {
    icon: Thermometer,
    title: "Temperature & Humidity",
    color: "#FF6B35",
    items: [
      "DHT11 sensor, GPIO 17, reads every 2 s",
      "Fan auto-on when temp > 30 °C or humidity > 70 %RH",
      "Buzzer alert when temp > 35 °C or humidity > 85 %RH",
      "dht_temp, dht_hum, dht_ok published with every reading",
      "Displayed on 20×4 LCD, dashboard stat card and gauge",
      "Works alongside PZEM load-based fan auto control",
    ],
  },
  {
    icon: Wind,
    title: "Air Quality Monitoring",
    color: "#00CCAA",
    items: [
      "MQ-135 gas sensor: CO₂, NH₃ and smoke (GPIO 34, ADC1_CH6)",
      "Voltage divider: AOUT → 10 kΩ → GPIO34 → 22 kΩ → GND",
      "5V × 22/32 = 3.44 V, safe for 12-bit ESP32 ADC",
      "GOOD (< 1500) / POOR (< 2500) / DANGER (≥ 2500) at 12-bit",
      "Configurable threshold via MQTT: {\"cmd\":\"gas_thresh\",\"val\":\"600\"}",
      "Auto fan + buzzer when threshold exceeded; beep latch resets on clear",
    ],
  },
  {
    icon: SlidersHorizontal,
    title: "Fan Auto & Manual Control",
    color: "#FFCC00",
    items: [
      "12V fan via IRLZ44N MOSFET and boost converter, GPIO 25",
      "LEDC channel 2, 25 Hz, 8-bit (0–255 duty)",
      "AUTO mode: 500 W → 30%, 1 500 W → 60%, 3 000 W → 100%",
      "Kickstart: 800 ms at full duty to overcome motor stiction",
      "Physical SPDT toggle switch on GPIO 13, manual 100% override",
      "MQTT: {\"cmd\":\"fan\",\"val\":\"auto|off|30|60|100\"}",
    ],
  },
  {
    icon: Lightbulb,
    title: "PIR Motion & Light Hub",
    color: "#FF6BD6",
    items: [
      "HC-SR501 PIR, GPIO 4 (VCC 5V, GND, OUT to GPIO 4)",
      "Light hub via IRLZ44N, GPIO 32, LEDC ch3, 1 kHz, 8-bit",
      "Motion: soft fade-in to 100%; no motion for 5 s: soft fade-out",
      "Manual override via MQTT sets lightManual flag; PIR won't auto-dim",
      "Buzzer beep on motion detection, configurable via buzzer_pir",
      "MQTT: {\"cmd\":\"light\",\"val\":\"off|on|25|50|100\"}",
    ],
  },
  {
    icon: Bell,
    title: "Buzzer Alert System",
    color: "#FF4444",
    items: [
      "5V active buzzer via TIP120 NPN Darlington, GPIO 18",
      "Phase loss → 5 rapid beeps (60 ms ON each)",
      "Voltage out of range (< 200 V or > 250 V) → 2 mid beeps",
      "Current > 15 A or total power > 5 000 W → 1 long low beep",
      "Gas DANGER → 3 error beeps; gas POOR → 2 mid beeps",
      "MQTT: {\"cmd\":\"buzzer\",\"val\":\"beep|alert|stop\"}",
    ],
  },
  {
    icon: Monitor,
    title: "20×4 LCD Display",
    color: "#FFD700",
    items: [
      "HD44780 20×4 LCD via I²C PCF8574A backpack, addr 0x27",
      "GPIO 21 (SDA) and GPIO 22 (SCL), safe if LCD not connected",
      "Row 0: WiFi status, MQTT status, fan %",
      "6-page cycle every 5 s: L1/L2/L3 voltage, current and power, plus DHT11, gas, Hz and PF",
      "Toggleable via MQTT: {\"cmd\":\"lcd\",\"val\":\"on|off\"}",
      "Web dashboard LCD terminal mirrors physical display live",
    ],
  },
  {
    icon: Bell,
    title: "Self-Test Diagnostic",
    color: "#3B9DFF",
    items: [
      "Runs once at startup, tests all 9 components in sequence",
      "Buzzer: 2 beeps (heard? → PASS), LCD: I²C address scan",
      "WiFi: connection + IP, MQTT: broker connected?",
      "PZEM L1/L2/L3: live voltage read (N/A = wiring or address error)",
      "MQ-135: raw ADC read + quality label",
      "Fan: kick 100% to 0%; did it spin? Results printed to Serial",
    ],
  },
  {
    icon: Wifi,
    title: "WiFi Failover",
    color: "#00CCAA",
    items: [
      "4 saved networks: phone hotspot, Raspberry Pi AP, Huawei LTE router, Rain 5G CPE",
      "Tries each in order with 15 s timeout per network",
      "Reconnects automatically if WiFi drops during operation",
      "IP shown on LCD row 0 and published in every MQTT packet",
      "Board keeps reading sensors even when offline, publishes on reconnect",
    ],
  },
  {
    icon: SlidersHorizontal,
    title: "Remote MQTT Control",
    color: "#9D7BFF",
    items: [
      "Command topic: campus/energy/pzem_cmd, QoS 1",
      "Fan: auto / off / 30 / 60 / 100 %",
      "Light hub: off / on / 25 / 50 / 100 %  (lightManual flag)",
      "LCD on/off, PIR enable/disable, gas threshold, buzzer on/off",
      "HTTP API also available: GET /api/pzem_cmd?cmd=x&val=y",
      "safeDelay() keeps mqtt.loop() alive during PZEM reads",
    ],
  },
  {
    icon: LineChart,
    title: "Cloud Analytics",
    color: "#6699FF",
    items: [
      "ThingSpeak Ch-A (3287764): V_L1/L2/L3, I_L1/L2/L3, W_L1 and W_L2 (8 fields)",
      "ThingSpeak Ch-B (3287770): W_L3, PF_L1/L2/L3, Hz_L1/L2/L3 and kWh_Total (8 fields)",
      "Grafana Cloud dashboards with live 15-second auto-refresh",
      "Historical trend charts built into the dashboard, 6 metrics and 3 phases",
      "Google Sheets export via Apps Script, uploading every 5 seconds",
    ],
  },
  {
    icon: Cloud,
    title: "Cloud Infrastructure",
    color: "#FF6B35",
    items: [
      "HiveMQ Cloud: MQTT TLS port 8883 (board) and WSS 8884 (browser)",
      "Cloudflare Pages: global CDN deployment, zero cold-start",
      "Firebase Authentication: email/password dashboard access",
      "React 18 + Tailwind CSS + Recharts + Framer Motion frontend",
      "ESP32 Arduino Core v2.0.17: PubSubClient and ArduinoJson",
    ],
  },
];

const HW_IMAGES = [
  ["/assets/fundani-hw-1.jpg", "Hardware Build 1", "ESP32-WROOM-32U, PZEM-004T × 3"],
  ["/assets/fundani-hw-2.jpg", "Hardware Build 2", "Wiring and enclosure assembly"],
  ["/assets/fundani-hw-3.jpg", "Hardware Build 3", "Sensor and actuator wiring"],
  ["/assets/fundani-hw-4.jpg", "Hardware Build 4", "MQ-135, DHT11 and HC-SR501 PIR"],
  ["/assets/fundani-hw-5.jpg", "Hardware Build 5", "20×4 LCD, buzzer and light hub"],
  ["/assets/fundani-hw-6.jpg", "Hardware Build 6", "Fan, IRLZ44N MOSFETs and power supply"],
  ["/assets/fundani-hw-7.jpg", "Hardware Build 7", "Final assembly, Cape Town"],
  ["/assets/fundani-hw-8.jpg", "Hardware Build 8", "Completed PZEM board enclosure"],
];


const BOM_IMAGES = [
  ["/assets/kit-flatlay.jpg",         "Component Flatlay",  "All parts before assembly"],
  ["/assets/bom-shopping.jpeg",       "Shopping List",      "Materials, glue & local suppliers"],
  ["/assets/bom-tools.png",           "Tools & Equipment",  "Pricing, quantity & purpose"],
  ["/assets/bom-overview.png",        "Components Overview","Component status and sourcing"],
];

const SENSOR_IMAGES = [
  { src: "/assets/diagrams/diag-dht-mq135-wiring.png", title: "DHT11 & MQ-135 Wiring Diagram",    desc: "GPIO connections, voltage divider and power rails" },
  { src: "/assets/hw-dht-mq135-test.jpeg",             title: "Live Sensor Test",                  desc: "Breadboard test setup, live readings verified" },
  { src: "/assets/hw-sensor-wiring.jpeg",              title: "TIP120 Driver Circuits",             desc: "Buzzer (GPIO 27), fan (GPIO 25) and light (GPIO 33) actuator wiring" },
];

const TECH_STACK = [
  { label: "ESP32-WROOM-32U",      desc: "Dual-core Xtensa LX6: WiFi, MQTT, UART, LEDC PWM" },
  { label: "PZEM-004T × 3",        desc: "Per-phase V / I / W / kWh / PF / Hz via Modbus-RTU" },
  { label: "DHT11",                desc: "Temp and humidity, GPIO 17, reads every 2 s" },
  { label: "MQ-135 Gas Sensor",    desc: "CO₂/NH₃/smoke, GPIO 34, 10kΩ and 22kΩ voltage divider" },
  { label: "HC-SR501 PIR",         desc: "Motion detection, GPIO 4, 5V supply" },
  { label: "IRLZ44N MOSFET × 2",  desc: "Fan (GPIO 25, 12V) and light hub (GPIO 32, 5V/12V), 100Ω gate" },
  { label: "TIP120 NPN Darlington",desc: "Buzzer driver, GPIO 18, 1kΩ base resistor, 5V" },
  { label: "20×4 I²C LCD",         desc: "HD44780 + PCF8574A backpack, addr 0x27, GPIO 21/22" },
  { label: "TTGO T-Camera v1.6",   desc: "OV2640 sensor, MQTT snapshots on demand, PIR auto-snap with 30 s cooldown" },
  { label: "5V Active Buzzer",     desc: "Phase-loss / voltage / current / gas / power alerts" },
  { label: "12V Boost Converter",  desc: "5V → 12V for fan, 1N4007 flyback diode" },
  { label: "HiveMQ Cloud",         desc: "Managed MQTT broker, TLS port 8883 for the board and WSS 8884 for the browser" },
  { label: "PubSubClient",         desc: "MQTT library: setBufferSize(1024) for ~520-byte PZEM JSON" },
  { label: "ArduinoJson",          desc: "StaticJsonDocument<900>: serialize/deserialize MQTT payloads" },
  { label: "Firebase Auth",        desc: "Email/password authentication for dashboard access" },
  { label: "Cloudflare Pages",     desc: "Global CDN deployment, zero cold-start, free tier" },
  { label: "React 18 + Tailwind",  desc: "SPA dashboard: dark/light theme, fully responsive" },
  { label: "ThingSpeak",           desc: "Dual IoT channels for V/I/W/PF/Hz/kWh history" },
  { label: "Grafana Cloud",        desc: "Professional time-series dashboards & alerting" },
];

export default function Features() {
  const hw      = useLightbox();
  const bom     = useLightbox();
  const sensors = useLightbox();

  const hwImages   = HW_IMAGES.map(([src, title, desc]) => ({ src, title, desc }));
  const bomImages  = BOM_IMAGES.map(([src, title, desc]) => ({ src, title, desc }));

  return (
    <PublicLayout>

      {/* ── OVERVIEW VIDEO ── */}
      <section className="border-b border-[var(--bd)]">
        <div className="max-w-[1300px] mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="uppercase tracking-[0.2em] text-xs font-bold text-[var(--accent)]">Project Overview</span>
            <h2 className="font-heading font-black text-4xl sm:text-5xl tracking-tight mt-2">Watch the system in action</h2>
            <p className="text-[var(--t2)] mt-3 text-base sm:text-lg">A guided walkthrough of EnergyLens, from hardware assembly to live dashboard.</p>
          </div>
          <div className="rounded-2xl overflow-hidden border border-[var(--bd)] shadow-2xl aspect-video">
            <iframe
              className="w-full h-full"
              src="https://www.youtube.com/embed/WO-YT1pTPYM"
              title="EnergyLens: Project Overview"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      </section>

      {/* ── FEATURE GROUPS ── */}
      <section className="border-b border-[var(--bd)] bg-[var(--surface)]/30">
        <div className="max-w-[1300px] mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="uppercase tracking-[0.2em] text-xs font-bold text-[var(--accent)]">Capabilities</span>
            <h2 className="font-heading font-black text-4xl sm:text-5xl tracking-tight mt-2">Everything an energy operator needs</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {FEATURE_GROUPS.map(({ icon: Icon, title, color, items }, i) => (
              <motion.div key={title} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} variants={fade}
                className="p-6 rounded-xl bg-[var(--surface)] border border-[var(--bd)] hover:border-[var(--bd-strong)] transition-colors">
                <div className="w-11 h-11 rounded-lg flex items-center justify-center mb-4" style={{ background: color + "18", color }}>
                  <Icon size={22} />
                </div>
                <h3 className="font-heading font-bold text-lg mb-3">{title}</h3>
                <ul className="space-y-2">
                  {items.map((it) => (
                    <li key={it} className="flex items-start gap-2 text-sm text-[var(--t2)]">
                      <CheckCircle2 size={14} className="mt-0.5 shrink-0" style={{ color }} /> {it}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── DHT11 & MQ-135 SENSOR SECTION ── */}
      {sensors.node}
      <section className="border-t border-[var(--bd)]">
        <div className="max-w-[1300px] mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="uppercase tracking-[0.2em] text-xs font-bold text-[var(--accent)]">Environmental Sensors</span>
            <h2 className="font-heading font-black text-4xl sm:text-5xl tracking-tight mt-2">DHT11 &amp; MQ-135: wiring and testing</h2>
            <p className="text-[var(--t2)] mt-3 text-base sm:text-lg leading-relaxed">How the temperature/humidity and gas sensors are connected to the ESP32, tested and verified before installation.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {SENSOR_IMAGES.map((img, i) => (
              <motion.div key={img.src} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} variants={fade}
                className="group rounded-xl overflow-hidden border border-[var(--bd)] bg-[var(--surface)] hover:border-[var(--accent)] transition-colors cursor-pointer"
                onClick={() => sensors.open(SENSOR_IMAGES, i)}>
                <div className="aspect-[4/3] overflow-hidden bg-black/20">
                  <img src={img.src} alt={img.title} loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="p-4">
                  <h3 className="font-heading font-bold text-base">{img.title}</h3>
                  <p className="text-sm text-[var(--t2)] mt-0.5 font-mono">{img.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── HARDWARE GALLERY ── */}
      {hw.node}
      <section className="border-t border-[var(--bd)] bg-[var(--surface)]/30">
        <div className="max-w-[1300px] mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="uppercase tracking-[0.2em] text-xs font-bold text-[var(--accent)]">Hardware</span>
            <h2 className="font-heading font-black text-4xl sm:text-5xl tracking-tight mt-2">Engineered enclosure v1.0</h2>
            <p className="text-[var(--t2)] mt-3 text-base sm:text-lg leading-relaxed">A custom-machined housing packing the ESP32-WROOM-32U, three PZEM-004T meters, DHT11 sensor, MQ-135 gas sensor, HC-SR501 PIR, buzzer and active cooling fan, all wired and built in Cape Town.</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {HW_IMAGES.map(([src, title, desc], i) => (
              <motion.div key={title} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i % 8} variants={fade}
                className="group rounded-xl overflow-hidden border border-[var(--bd)] bg-[var(--surface)] hover:border-[var(--accent)] transition-colors cursor-pointer"
                onClick={() => hw.open(hwImages, i)}>
                <div className="aspect-square overflow-hidden bg-black/30">
                  <img src={src} alt={title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                </div>
                <div className="p-3">
                  <h3 className="font-heading font-bold text-sm sm:text-base">{title}</h3>
                  <p className="text-xs sm:text-sm text-[var(--t2)] mt-0.5 font-mono">{desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TECH STACK ── */}
      <section className="border-t border-[var(--bd)]">
        <div className="max-w-[1300px] mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <div className="text-center max-w-xl mx-auto mb-8">
            <span className="uppercase tracking-[0.2em] text-xs font-bold text-[var(--accent)]">Tech Stack</span>
            <h2 className="font-heading font-black text-4xl sm:text-5xl tracking-tight mt-2">Built on proven technology</h2>
          </div>
          <div className="flex flex-wrap gap-2 justify-center">
            {TECH_STACK.map(({ label }) => (
              <span key={label} className="px-4 py-2 rounded-full text-sm font-mono bg-[var(--surface)] border border-[var(--bd)] text-[var(--t2)]">{label}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ── BOM & BUILD DOCS ── */}
      {bom.node}
      <section className="border-t border-[var(--bd)] bg-[var(--surface)]/30">
        <div className="max-w-[1300px] mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="uppercase tracking-[0.2em] text-xs font-bold text-[var(--accent)]">Build Documentation</span>
            <h2 className="font-heading font-black text-4xl sm:text-5xl tracking-tight mt-2">Bill of materials & cost sheets</h2>
            <p className="text-[var(--t2)] mt-3 text-base sm:text-lg">Sourced locally in Cape Town. Click any sheet to view full size.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {BOM_IMAGES.map(([src, title, desc], i) => (
              <motion.div key={title}
                initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} variants={fade}
                className="group rounded-xl overflow-hidden border border-[var(--bd)] bg-[var(--surface)] hover:border-[var(--accent)] transition-colors cursor-pointer"
                onClick={() => bom.open(bomImages, i)}>
                <div className="aspect-[4/3] overflow-hidden bg-white">
                  <img src={src} alt={title} className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.03]" loading="lazy" />
                </div>
                <div className="p-4 flex items-center justify-between gap-2">
                  <div>
                    <h4 className="font-heading font-bold text-base">{title}</h4>
                    <p className="text-sm text-[var(--t2)] mt-0.5 font-mono">{desc}</p>
                  </div>
                  <ArrowRight size={14} className="text-[var(--accent)] shrink-0 -rotate-45 group-hover:rotate-0 transition-transform" />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="border-t border-[var(--bd)]">
        <div className="max-w-[1300px] mx-auto px-4 sm:px-6 py-16 sm:py-20 text-center">
          <h2 className="font-heading font-black text-4xl sm:text-5xl tracking-tight">Ready to explore the live system?</h2>
          <p className="text-[var(--t2)] mt-3 text-base sm:text-lg">Create an account and open the real-time dashboard in seconds.</p>
          <div className="flex justify-center gap-3 flex-wrap mt-8">
            <Link to="/register" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-md bg-[var(--accent)] text-black font-semibold active:scale-95 transition-transform">
              <Zap size={18} /> Launch Dashboard
            </Link>
            <Link to="/about" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-md border border-[var(--bd)] font-semibold hover:border-[var(--bd-strong)] transition-colors">
              Meet the Engineer <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

    </PublicLayout>
  );
}
